import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, AlertCircle, Award, CalendarDays, Star, Loader2, ArrowLeft, TrendingUp } from 'lucide-react';
import { LoadingBar } from '../Layout/LoadingBar';
import { FullPageLoader } from '../Common/FullPageLoader';
import { useLanguage } from '../../context/LanguageContext';
import { Player, Game } from '../../types';
import { getPlayerDetails, fetchTeamRecentMatches } from '../../services/footballService';
import { getLeagueName, getLeagueNameFromAny } from '../../services/leagueService';
import { getTeamName } from '../../services/teamService';
import { t, tr } from '../../i18n';
import { PlayerHeader } from '../Player/PlayerHeader';
import { TransferHistoryTimeline } from '../Player/TransferHistoryTimeline';
import { PlayerStatsChart } from '../Player/PlayerStatsChart';
import SEO from '../Common/SEO';

interface PlayerDetailViewProps {
    playerId?: string;
    onBack: () => void;
    onNavigateToGame: (game: Game) => void;
}

export const PlayerDetailView: React.FC<PlayerDetailViewProps> = ({ playerId, onBack, onNavigateToGame }) => {
  const { lang } = useLanguage();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMetric, setActiveMetric] = useState<'goals' | 'assists' | 'appearances' | 'rating'>('goals');
  
  const [recentMatches, setRecentMatches] = useState<Game[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [activeCompFilter, setActiveCompFilter] = useState('');

  const fetchPlayer = async () => {
    setLoading(true);
    try {
        const data = await getPlayerDetails(playerId || 'search-Lamine Yamal');
        if (data) {
            setPlayer(data);
        } else {
            setError("Player not found");
        }
    } catch (e) { 
        setError("Failed to load data"); 
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPlayer();
    setRecentMatches([]);
    setActiveCompFilter('');
  }, [playerId]);

  useEffect(() => {
    if (!player || !player.currentTeamId) return;
    const loadRecentMatches = async () => {
        setMatchesLoading(true);
        try {
            const matches = await fetchTeamRecentMatches(player.currentTeamId!);
            setRecentMatches(matches);
        } catch (e) {
            console.error("Recent matches fetch error:", e);
        }
        setMatchesLoading(false);
    };
    loadRecentMatches();
  }, [player]);

  // 중요도 기반 정렬 함수
  const getCompWeight = (name: string) => {
      const n = name.toLowerCase();
      if (n.includes('champions league')) return 100;
      if (n.includes('europa league')) return 90;
      if (n.includes('conference league')) return 80;
      if (n.includes('premier league') || n.includes('laliga') || n.includes('serie a') || n.includes('bundesliga') || n.includes('ligue 1')) return 70;
      if (n.includes('cup') || n.includes('trophy') || n.includes('shield')) return 50;
      return 10;
  };

  const statsList = useMemo(() => {
    if (!player) return [];
    return (player.detailedStats || []).filter(s => s.teamId === player.currentTeamId);
  }, [player]);

  const competitions = useMemo(() => {
      return Array.from(new Set(statsList.map(s => s.competition)))
                  .sort((a: string, b: string) => getCompWeight(b) - getCompWeight(a));
  }, [statsList]);

  // 데이터 로드 시 첫 번째 대회를 기본 필터로 설정
  useEffect(() => {
      if (competitions.length > 0 && (!activeCompFilter || !competitions.includes(activeCompFilter))) {
          setActiveCompFilter(competitions[0]);
      }
  }, [competitions, activeCompFilter]);

  const getMatchResult = (match: Game) => {
      if (!match.score || !player?.currentTeamId) return null;
      const scores = match.score.split(':').map(Number);
      const isHome = match.homeTeam.id === player.currentTeamId;
      const teamScore = isHome ? scores[0] : scores[1];
      const opponentScore = isHome ? scores[1] : scores[0];
      if (teamScore > opponentScore) return { label: 'W', color: 'bg-emerald-500' };
      if (teamScore === opponentScore) return { label: 'D', color: 'bg-gray-400' };
      return { label: 'L', color: 'bg-rose-500' };
  };

  if (loading) return <FullPageLoader isLoading={true} icon={Search} text={t(lang, 'analyzingTalent')} />;
  if (error || !player) return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-white">
          <AlertCircle size={48} className="text-rose-500 mb-4" />
          <h2 className="text-xl font-bold">{t(lang, 'articleNotFound')}</h2>
          <button onClick={onBack} className="mt-4 text-rose-500 font-bold md:hover:underline">{t(lang, 'goBack')}</button>
      </div>
  );
  
  const latestSeason = player.stats[0];
  return (
    <div className="pb-32 min-h-screen bg-white relative">
      <div className="fixed top-0 left-0 right-0 z-[150] pointer-events-none md:absolute">
          <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
              <button 
                  onClick={onBack}
                  className="pointer-events-auto w-10 h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 flex items-center justify-center bg-white/80 backdrop-blur-xl border border-gray-100 rounded-full text-slate-900 shadow-xl shadow-black/5 active:scale-90 transition-all md:hover:bg-white md:hover:border-rose-200 md:hover:text-rose-500"
              >
                  <ArrowLeft className="w-5 h-5 md:w-5.5 md:h-5.5 lg:w-6 lg:h-6" strokeWidth={3} />
              </button>
          </div>
      </div>
            {player && (
                <SEO
                    title={tr(lang as any, 'playerDetailSeoTitle', { playerName: player.name, position: player.position || '', team: player.team || '' })}
                    description={tr(lang as any, 'playerDetailSeoDesc', { playerName: player.name })}
                />
            )}
      <LoadingBar isLoading={loading || matchesLoading} />
      
      <PlayerHeader player={player} lang={lang} onBack={onBack} />

      {/* Season Summary Section - Hidden if no stats */}
      {latestSeason && (
          <div className="max-w-5xl mx-auto px-6 -mt-8 relative z-30">
            <div className="bg-white rounded-[24px] border border-gray-100 flex flex-wrap justify-around items-center p-8 gap-6 shadow-sm">
                {[
                    { label: t(lang, 'goals'), value: latestSeason.goals, color: 'text-rose-500' },
                    { label: t(lang, 'assistsFor'), value: latestSeason.assists, color: 'text-slate-900' },
                    { label: t(lang, 'played'), value: latestSeason.appearances, color: 'text-slate-900' },
                    { label: t(lang, 'rating'), value: Number(latestSeason.rating).toFixed(2), color: 'text-amber-500', isRating: true }
                ].map((item, i) => (
                    <React.Fragment key={i}>
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                                {item.isRating && <Star size={14} className="text-amber-400 fill-amber-400" />}
                                <span className={`text-2xl md:text-3xl font-black ${item.color} tracking-tight tabular-nums`}>{item.value}</span>
                            </div>
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mt-1">{item.label}</span>
                        </div>
                        {i < 3 && <div className="hidden sm:block w-px h-8 bg-gray-100"></div>}
                    </React.Fragment>
                ))}
            </div>
          </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-12">

        <div className="grid grid-cols-1 gap-12">
            <div className="space-y-16">
                
                {/* 5-Year History Chart - Hidden if empty */}
                {player.stats.length > 0 && (
                    <section>
                        <PlayerStatsChart player={player} lang={lang} metric={activeMetric} onChangeMetric={setActiveMetric} />
                    </section>
                )}

                {/* Season Breakdown - Hidden if empty */}
                {statsList.length > 0 && (
                    <section>
                        <div className="mb-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-1 h-6 bg-slate-900 rounded-full"></div>
                                <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase">{t(lang, 'detailedSeasonBreakdown')}</h2>
                            </div>
                            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                                {competitions.map(comp => {
                                    const matchingStat = statsList.find(s => s.competition === comp);
                                        const displayName = matchingStat?.competitionId 
                                        ? getLeagueName(parseInt(matchingStat.competitionId), lang, comp)
                                        : getLeagueNameFromAny(comp, lang, comp);
                                    
                                    return (
                                        <button 
                                            key={comp} 
                                            onClick={() => setActiveCompFilter(comp)} 
                                            className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${
                                                activeCompFilter === comp ? 'bg-slate-900 text-white shadow-md' : 'bg-gray-50 text-slate-400 md:hover:bg-gray-100'
                                            }`}
                                        >
                                            {displayName}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="space-y-3">
                            {statsList
                                .filter(s => s.competition === activeCompFilter)
                                .map((stat, i) => (
                                <div key={i} className="flex justify-between items-center bg-white p-5 rounded-2xl border border-gray-100 md:hover:border-rose-100 transition-all">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <div className="w-10 h-10 p-2 bg-gray-50 rounded-xl flex items-center justify-center shrink-0">
                                            <img src={stat.competitionLogo} className="w-full h-full object-contain" alt="" onError={(e) => { e.currentTarget.style.display='none'; }} />
                                        </div>
                                        <div className="min-w-0">
                                            <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest">{stat.season}</span>
                                            <h4 className="font-black text-xs text-slate-900 leading-tight truncate uppercase">
                                                {stat.competitionId ? getLeagueName(parseInt(stat.competitionId), lang, stat.competition) : getLeagueNameFromAny(stat.competition, lang, stat.competition)}
                                            </h4>
                                            <p className="text-[9px] font-bold text-slate-300 truncate">
                                                {getTeamName(stat.teamId, lang, stat.team)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6 shrink-0">
                                        <div className="text-center w-8">
                                            <span className="block font-black text-sm text-slate-900">{stat.goals}</span>
                                            <span className="text-[7px] text-slate-300 font-black uppercase">G</span>
                                        </div>
                                        <div className="text-center w-8">
                                            <span className="block font-black text-sm text-slate-900">{stat.assists}</span>
                                            <span className="text-[7px] text-slate-300 font-black uppercase">A</span>
                                        </div>
                                        <div className="text-center w-8">
                                            <span className="block font-black text-sm text-slate-400">{stat.appearances}</span>
                                            <span className="text-[7px] text-slate-300 font-black uppercase">M</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Career Path - Hidden if empty */}
                {player.history.length > 0 && (
                    <TransferHistoryTimeline history={player.history} lang={lang} title={t(lang, 'transferHistory')} />
                )}
            </div>

            {/* Sidebar area - Hidden if empty */}
            {recentMatches.length > 0 && (
                <div className="space-y-12">
                    <section>
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-1 h-6 bg-slate-900 rounded-full"></div>
                            <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase leading-none">{t(lang, 'recentForm')}</h2>
                        </div>

                        <div className="space-y-3">
                            {matchesLoading ? (
                                <div className="py-12 text-center">
                                    <Loader2 className="animate-spin text-rose-500" size={24} />
                                </div>
                            ) : (
                                recentMatches.map(match => {
                                    const result = getMatchResult(match);
                                    const isHome = match.homeTeam.id === player.currentTeamId;
                                    const opponent = isHome ? match.awayTeam : match.homeTeam;
                                    
                                    return (
                                        <div 
                                            key={match.id} 
                                            onClick={() => onNavigateToGame(match)} 
                                            className="p-4 bg-white border border-gray-100 md:hover:border-rose-100 md:hover:shadow-lg md:hover:shadow-rose-50/50 rounded-2xl transition-all cursor-pointer flex items-center gap-4 group active:scale-[0.98]"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 p-1.5 bg-gray-50 rounded-lg border border-gray-100 shrink-0">
                                                        <img 
                                                            src={opponent.logo} 
                                                            className="w-full h-full object-contain" 
                                                            alt="" 
                                                            onError={(e) => { e.currentTarget.style.opacity='0.2'; }}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[8px] font-black text-rose-500 uppercase tabular-nums">{match.dateLabel}</span>
                                                            <div className="w-1 h-1 rounded-full bg-slate-100"></div>
                                                            <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest truncate">
                                                                {getLeagueName(parseInt(match.league), lang, match.leagueName)}
                                                            </span>
                                                        </div>
                                                        <p className="text-md font-black text-slate-900 truncate md:group-hover:text-rose-600 transition-colors uppercase tracking-tight">
                                                            {getTeamName(opponent.id, lang, opponent.name)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="text-right shrink-0">
                                                <span className="text-xs font-black text-slate-900 tabular-nums px-2 py-1 rounded-lg">
                                                    {match.score || '-'}
                                                </span>
                                            </div>

                                            <div className={`w-6 h-6 shrink-0 ${result?.color || 'bg-slate-200'} text-white text-[10px] font-black rounded-lg flex items-center justify-center shadow-sm`}>
                                                {result?.label || '-'}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </section>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};
